import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Box } from '@mui/material';
import { styles } from './ProgressTracker.styles';
import { IEditorMessages, IUpdateStoryContentProps } from '../../../../types';
import { IStoryContent, IStory, IProgressTracker, TimelineChartOrientation, EventRow } from '@opengov/stories-shared/types';
import { addMedia, removeMedia } from '../../../utils/dom';
import { TIMELINE_CHART } from '../../../constants';
import { EventsList, IUpdateEventPayload } from './EventsList';
import { ProgressChart } from './ProgressChart';
import { TitleEditing } from './TitleEditing';
import { PreviewSwitch } from './PreviewSwitch';
import { PROGRESS_TRACKER_MODES, ProgressTrackerMode } from './constants';
import { useProgressTrackerSettingsContext } from '../../StoriesSettings/ProgressTrackerSettings/context/useProgressTrackerSettingsContext';


/**
 * Safely casts a string to EventStatus type
 * @param status - The status string to validate
 * @returns The validated EventStatus or 'Not Started' as default
 */

interface ProgressTrackerProps {
	messages: IEditorMessages;
	saveProgressTrackerConfig: (props: IProgressTracker) => Promise<void>;
	storyContent: IStoryContent;
	story: IStory;
	isTemplate?: boolean;
	updateStoryContent?: (storyContent: IStoryContent) => void;
	updateStoryContentDB?: (storyContent: IUpdateStoryContentProps) => void;
	initialValue?: IProgressTracker;
	mode?: ProgressTrackerMode;
}

export const ProgressTracker = React.memo(({
	messages,
	story,
	updateStoryContent,
	updateStoryContentDB,
	saveProgressTrackerConfig,
	storyContent,
	initialValue,
	mode = 'editWithConfig'
}: ProgressTrackerProps) => {
	const [isEditingTitle, setIsEditingTitle] = useState(false);
	const { progressTrackerSettings: ProgressTrackerSettings } = useProgressTrackerSettingsContext();
	const [progressTrackerValues, setProgressTrackerValues] = useState<IProgressTracker>(initialValue || {
		storyId: story?._id || '',
		contentId: storyContent?._id || '',
		type: 'timeline',
		config: {
			showPreview: false,
			title: messages.progressTracker,
			orientation: TimelineChartOrientation.HORIZONTAL,
			events: Array.from({ length: 3 }, (_, index) => ({
				id: `${index + 1}`,
				title: '',
				description: '',
				status: {
					id: ProgressTrackerSettings[0].id,
					title: ProgressTrackerSettings[0].status
				}
			}))
		}
	});


	useEffect(() => {
		if (initialValue) {
			setProgressTrackerValues(initialValue);
		}
	}, [initialValue]);

	const events = useMemo(() => {
		if (progressTrackerValues?.config?.events?.length) {
			return progressTrackerValues.config.events.map((event, index) => ({
				id: `existing-${index}`,
				title: event.title,
				description: event.description,
				status: event.status
			}));
		}
		return [];
	}, [progressTrackerValues?.config?.events]);

	const saveEventsConfig = async (values: IProgressTracker) => {
		const { storyId, contentId, id } = values;
		if (storyContent && updateStoryContent && updateStoryContentDB && story) {
			if (!id) {
				const uniqueId = uuidv4();
				const progressTrackerId = `progress-tracker-${uniqueId}`;
				const mediaDiv = `<div
					tabIndex="0"
					aria-label="Progress Tracker"
					class="progressTracker"
					data-media-type="progress-tracker"
					id="${progressTrackerId}"
				/>`;
				const metaStoryContent = removeMedia(storyContent, 0);
				const finalStoryContent: IStoryContent = addMedia(metaStoryContent, mediaDiv, 0, TIMELINE_CHART);
				await updateStoryContent(finalStoryContent);
				await updateStoryContentDB({ story: story, isTemplate: false, storyContent: finalStoryContent });
			}
			await saveProgressTrackerConfig({
				storyId,
				contentId,
				config: {
					title: values.config.title,
					showPreview: values.config.showPreview,
					events: values.config.events,
					orientation: values.config.orientation
				},
				type: 'timeline',
				...(initialValue?.id && { id: initialValue.id })
			});
		}
	}

	const handleTitleChange = useCallback((value: string) => {
		setProgressTrackerValues(prev => ({
			...prev,
			config: {
				...prev.config,
				title: value
			}
		}));
	}, []);

	const handleOrientationChange = useCallback((orientation: TimelineChartOrientation) => {
		setProgressTrackerValues(prev => ({
			...prev,
			config: { ...prev.config, orientation }
		}));
	}, []);

	const handleSaveTitle = useCallback(async () => {
		setIsEditingTitle(false);
		if (story?._id && storyContent?._id) {
			const configToSave: IProgressTracker = {
				storyId: story._id,
				contentId: storyContent._id,
				type: 'timeline',
				config: {
					title: progressTrackerValues?.config.title,
					showPreview: progressTrackerValues?.config.showPreview,
					events: progressTrackerValues?.config.events,
					orientation: progressTrackerValues?.config.orientation || TimelineChartOrientation.HORIZONTAL
				},
				...(initialValue?.id && { id: initialValue.id })
			};
			await saveEventsConfig(configToSave);
		}
	}, [progressTrackerValues.config.title, progressTrackerValues.config.showPreview, progressTrackerValues.config.events, story?._id, storyContent?._id, initialValue?.id]);

	const handleCancelTitle = useCallback(() => {
		setIsEditingTitle(false);
		if (initialValue?.config?.title) {
			setProgressTrackerValues(prev => ({
				...prev,
				config: {
					...prev.config,
					title: initialValue.config.title
				}
			}));
		}
	}, [initialValue?.config?.title]);

	const handleEditTitle = useCallback(() => {
		setIsEditingTitle(true);
	}, []);

	const handleEventsChange = useCallback((updatedEvents: EventRow[]) => {
		const transformedEvents = updatedEvents.map(event => ({
			title: event.title,
			description: event.description,
			status: event.status
		}));
		const updatedConfig = {
			...progressTrackerValues,
			config: {
				...progressTrackerValues.config,
				events: transformedEvents
			}
		};
		setProgressTrackerValues(updatedConfig);
	}, [progressTrackerValues]);

	const handleUpdateEvent = useCallback((id: string, field: keyof EventRow, value: string) => {
		if (field === 'status') {
			const status = ProgressTrackerSettings.find(status => status.id === value);
			if (status) {
				const updatedEvents = events.map(event =>
					event.id === id ? { ...event, status: { id: status.id, title: status.status } } : event
				);
				handleEventsChange(updatedEvents);
			}
		}
		else {
			const updatedEvents = events.map(event =>
				event.id === id ? { ...event, [field]: value } : event
			);
			handleEventsChange(updatedEvents);
		}
	}, [events, handleEventsChange]);

	const handleRemoveEvent = useCallback((id: string) => {
		const updatedEvents = events.filter(event => event.id !== id);
		handleEventsChange(updatedEvents);
	}, [events, handleEventsChange]);

	const handleAddEvent = useCallback(async () => {
		const newEvent: EventRow = {
			id: `event-${Date.now()}`,
			title: '',
			description: '',
			status: {
				id: ProgressTrackerSettings[0].id,
				title: ProgressTrackerSettings[0].status
			}
		};
		const updatedEvents = [...events, newEvent];
		handleEventsChange(updatedEvents);
	}, [events, handleEventsChange]);

	const handleTogglePreview = useCallback(async () => {
		const newShowPreview = !progressTrackerValues.config.showPreview;
		const updatedConfig = {
			...progressTrackerValues,
			config: {
				...progressTrackerValues.config,
				showPreview: newShowPreview,
			}
		};
		setProgressTrackerValues(updatedConfig);
		// Save config only when toggling preview to true
		if (newShowPreview && story?._id && storyContent?._id) {
			const configToSave: IProgressTracker = {
				storyId: story._id,
				contentId: storyContent._id,
				type: 'timeline',
				config: {
					title: progressTrackerValues.config.title,
					showPreview: newShowPreview,
					events: progressTrackerValues.config.events,
					orientation: progressTrackerValues.config.orientation
				},
				...(initialValue?.id && { id: initialValue.id })
			};
			await saveEventsConfig(configToSave);
		}
	}, [progressTrackerValues, story?._id, storyContent?._id, initialValue?.id, saveEventsConfig]);

	const titleEditingProps = useMemo(() => ({
		isEditing: isEditingTitle,
		title: progressTrackerValues.config.title,
		onTitleChange: handleTitleChange,
		onSave: handleSaveTitle,
		onCancel: handleCancelTitle,
		onEdit: handleEditTitle,
		messages,
		readOnly: mode === PROGRESS_TRACKER_MODES.READ_ONLY
	}), [isEditingTitle, progressTrackerValues.config.title, handleTitleChange, handleSaveTitle, handleCancelTitle, handleEditTitle, messages, mode]);

	const previewSwitchProps = useMemo(() => ({
		showPreview: progressTrackerValues.config.showPreview,
		onToggle: handleTogglePreview,
		label: messages.showPreviewLabel,
		orientationLabel: messages.orientationLabel,
		onOrientationChange: handleOrientationChange,
		orientation: progressTrackerValues.config.orientation,
		disabled: mode === PROGRESS_TRACKER_MODES.READ_ONLY
	}), [progressTrackerValues.config.showPreview, handleTogglePreview, messages.showPreviewLabel, mode]);

	const renderContent = () => {
		if (mode === PROGRESS_TRACKER_MODES.READ_ONLY || progressTrackerValues.config.showPreview) {
			const chartEvents = progressTrackerValues?.config?.events?.map((event, index) => ({
				id: `chart-event-${index}`,
				title: event.title,
				description: event.description,
				status: event.status
			})) || events;
			return <ProgressChart events={chartEvents} orientation={progressTrackerValues.config.orientation} />;
		}

		// Single EventsList component handles both existing and new events
		return (
			<EventsList
				events={events}
				messages={messages}
				onUpdateEvent={(payload: IUpdateEventPayload) => handleUpdateEvent(payload.id, payload.field, payload.value)}
				onRemoveEvent={handleRemoveEvent}
				onAddEvent={handleAddEvent}
			/>
		);
	};
	return (
		<>
			<Box sx={styles.progressTrackerContainer}>
				<Box sx={styles.progressTrackerBox}>
					<Box sx={styles.eventListHeader}>
						<TitleEditing {...titleEditingProps} />
					</Box>
					{mode !== PROGRESS_TRACKER_MODES.READ_ONLY ?
						<PreviewSwitch {...previewSwitchProps} />
						: null}
				</Box>
				{renderContent()}
			</Box>
		</>
	);
});

ProgressTracker.displayName = 'ProgressTracker';
